import { parseBoard, replay } from "./lot";
import {
  MAX_MOVES,
  MOVE_PATTERN,
  type RushDefinition,
  type RushProgress,
  type RushSubmission,
} from "./types";

export const DEFAULT_RULES = {
  en: "Slide the vehicles, forwards or backwards along their own lane, until the red car can drive out through the exit on the right. Vehicles never turn and never jump over one another. A slide of any length counts as one move.",
  fr: "Faites glisser les véhicules, en avant ou en arrière dans leur propre file, jusqu'à ce que la voiture rouge puisse sortir par la droite. Un véhicule ne tourne jamais et ne saute par-dessus aucun autre. Un glissement, quelle que soit sa longueur, compte pour un coup.",
};

/** The same starting lot as the backend's `empty_definition()`. */
export const STARTER_BOARD = ["......", "......", "AA.B..", "...B..", "......", "......"];

export function emptyDefinition(): RushDefinition {
  return {
    puzzleType: "rush_hour",
    typeVersion: 1,
    board: [...STARTER_BOARD],
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: RushDefinition): RushProgress {
  return { version: 1, moves: [], undone: [] };
}

/** Keeps only well-formed slides, as many as the schema takes. */
export function sanitizeMoves(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((move): move is string => typeof move === "string" && MOVE_PATTERN.test(move))
    .slice(0, MAX_MOVES);
}

/**
 * Accepts any stored blob (older versions, null, garbage) and returns a log this lot can
 * actually play: a log recorded against another definition is cut at the first slide that
 * no longer works, rather than leaving the lot in a position nobody can reach.
 */
export function migrateProgress(definition: RushDefinition, raw: unknown): RushProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const parsed = parseBoard(definition.board);
  if (!parsed) return emptyProgress(definition);
  const source = raw as { moves?: unknown; undone?: unknown };
  const outcome = replay(parsed.lot, parsed.state, sanitizeMoves(source.moves));
  return {
    version: 1,
    moves: outcome.moves,
    undone: outcome.illegalAt === null ? sanitizeMoves(source.undone) : [],
  };
}

/** The server replays the same slides: what the player did, and nothing else. */
export function serializeSubmission(
  _definition: RushDefinition,
  progress: RushProgress,
): RushSubmission {
  return { moves: [...progress.moves] };
}
